import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { SkloKruh } from '../../ui/Sklo';
import ZoomPanViewer from '../../ui/ZoomPanViewer';
import { Z_NAD_LISTOU } from '../../ui/ModalShell';
import { ROZOSTRENI_NAVIGACE } from '../../../lib/uiTokens';

/**
 * Fotky a videa příloh přes celou obrazovku — stejný jazyk jako prohlížeč
 * reportu (standard-reporty.md §4): tmavé pozadí, plovoucí ovládání v tmavém
 * skle, safe-area. Fotka jde přiblížit prsty i dvojklikem, mezi přílohami se
 * listuje šipkami, klávesami nebo tahem do strany.
 */
const SKLO = {
    background: 'rgba(28,28,28,0.74)',
    backdropFilter: ROZOSTRENI_NAVIGACE,
    WebkitBackdropFilter: ROZOSTRENI_NAVIGACE,
    boxShadow: '0 6px 20px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.14)',
    color: '#FFFFFF',
};

export default function ProhlizecPriloh({ prilohy, index = 0, onClose }) {
    const [i, setI] = useState(index);
    const p = prilohy[Math.min(i, prilohy.length - 1)];
    const tah = useRef(null);
    const vice = prilohy.length > 1;

    const dalsi = () => setI(x => (x + 1) % prilohy.length);
    const predchozi = () => setI(x => (x - 1 + prilohy.length) % prilohy.length);

    useEffect(() => {
        const predtim = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const klavesa = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight') dalsi();
            if (e.key === 'ArrowLeft') predchozi();
        };
        document.addEventListener('keydown', klavesa);
        return () => { document.body.style.overflow = predtim; document.removeEventListener('keydown', klavesa); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Tah jedním prstem do strany = další / předchozí (jen bez přiblížení —
    // přiblíženou fotku posouvá ZoomPanViewer a tah tam nedojde).
    const zacatek = (e) => { if (e.touches.length === 1) tah.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
    const konec = (e) => {
        if (!tah.current || !vice) return;
        const dx = e.changedTouches[0].clientX - tah.current.x;
        const dy = e.changedTouches[0].clientY - tah.current.y;
        tah.current = null;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) (dx < 0 ? dalsi : predchozi)();
    };

    if (!p) return null;
    const okraj = 16;

    return createPortal((
        <div role="dialog" aria-modal="true" aria-label={p.nazev}
            style={{ position: 'fixed', inset: 0, zIndex: Z_NAD_LISTOU + 10, background: '#0E0E0E' }}
            onTouchStart={zacatek} onTouchEnd={konec}>

            <div style={{
                position: 'absolute', inset: 0,
                paddingTop: 'calc(env(safe-area-inset-top, 0px) + 68px)',
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
                paddingLeft: `calc(env(safe-area-inset-left, 0px) + ${okraj}px)`,
                paddingRight: `calc(env(safe-area-inset-right, 0px) + ${okraj}px)`,
            }}>
                {p.je_video ? (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <video key={p.id} src={p.url} controls playsInline autoPlay style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 6 }} />
                    </div>
                ) : (
                    <ZoomPanViewer resetKey={p.id} kolecko="vzdy">
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src={p.url} alt={p.nazev} draggable={false}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }} />
                        </div>
                    </ZoomPanViewer>
                )}
            </div>

            {/* Horní ovládání: zavřít · název a pořadí · stáhnout */}
            <div style={{
                position: 'absolute', left: 0, right: 0, top: 0, pointerEvents: 'none',
                paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
                paddingLeft: `calc(env(safe-area-inset-left, 0px) + ${okraj}px)`,
                paddingRight: `calc(env(safe-area-inset-right, 0px) + ${okraj}px)`,
                display: 'flex', alignItems: 'center', gap: 10,
            }}>
                <SkloKruh Icon={X} popis="Zavřít" onClick={onClose} style={{ ...SKLO, pointerEvents: 'auto' }} />
                <div className="truncate" style={{
                    ...SKLO, pointerEvents: 'auto', margin: '0 auto', minWidth: 0, maxWidth: '60%',
                    height: 44, borderRadius: 999, padding: '0 16px', display: 'flex', alignItems: 'center',
                    fontSize: 13, fontWeight: 600,
                }}>
                    <span className="truncate">{vice ? `${i + 1} / ${prilohy.length} · ` : ''}{p.nazev}</span>
                </div>
                <SkloKruh Icon={Download} popis="Stáhnout" href={p.stahnout_url} style={{ ...SKLO, pointerEvents: 'auto' }} />
            </div>

            {vice && (
                <>
                    <SkloKruh Icon={ChevronLeft} popis="Předchozí" onClick={predchozi}
                        style={{ ...SKLO, position: 'absolute', left: `calc(env(safe-area-inset-left, 0px) + ${okraj}px)`, top: '50%', transform: 'translateY(-50%)' }} />
                    <SkloKruh Icon={ChevronRight} popis="Další" onClick={dalsi}
                        style={{ ...SKLO, position: 'absolute', right: `calc(env(safe-area-inset-right, 0px) + ${okraj}px)`, top: '50%', transform: 'translateY(-50%)' }} />
                </>
            )}
        </div>
    ), document.body);
}
